/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormLabel,
} from "@mui/material";
import { useFormik } from "formik";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import { RootState, useAppDispatch } from "@store/store";
import { setLoanStatus, setShareHolderData } from "@store/slices/registerSlice";
import { useGetPersonTypeQuery, useLazyGetStatesQuery } from "@api/catalogs";
import { useSaveShareholderDataMutation } from "@api/onboarding";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { ProgressBar } from "@components/ProgressBar";
import { transformTitleCase } from "@helpers/funciones";
import {
  PayloadPersonPatch,
  PayloadSaveShareholderData,
  ResponseSaveShareholderData,
  StateStructure,
} from "@interfaces/index";
import { usePostValidateNextStageMutation } from "@api/applications";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { usePatchPersonMutation } from "@api/personasApi";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";

const MAX_DATE = dayjs().subtract(18, "years");
const SCREEN_STATUS = "CSDAL";

const DatosAccionista = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const shareholderData = useSelector((state: RootState) => state.register.shareholder);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const addressData = useSelector((state: RootState) => state.register.address_data);

  const dispatch = useAppDispatch();
  const [getStatesTrigger] = useLazyGetStatesQuery();
  const [saveShareholderData] = useSaveShareholderDataMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [patchPerson] = usePatchPersonMutation();
  const { data: personTypeData } = useGetPersonTypeQuery();

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [personTypeId, setPersonTypeId] = useState("");
  const [shareholderId, setShareholderId] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [placesCatalog, setPlacesCatalog] = useState<StateStructure[]>([]);

  useEffect(() => {
    if (personTypeData) {
      const filteredItem = personTypeData.find((item) => item.code === "PTAM");
      setPersonTypeId(String(filteredItem?.id));
    }
  }, [personTypeData]);

  const formik = useFormik({
    initialValues: {
      majority_shareholder: false,
      name: shareholderData.name || "",
      first_lastname: shareholderData.first_lastname || "",
      second_lastname: shareholderData.second_lastname || "",
      date_birth: "",
      birth_state: shareholderData.birth_state || "",
      email: shareholderData.email || "",
      sex: shareholderData.sex || "",
      code_phone_number: shareholderData.code_phone_number || "+52",
      phone_number: shareholderData.phone_number || "",
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Campo requerido"),
      first_lastname: yup.string().required("Campo requerido"),
      second_lastname: yup.string(),
      date_birth: yup
        .string()
        .required("Campo requerido")
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .test("is-adult", "Debes ser mayor de 18 años", (value) => {
          if (!value) return false;

          const birthDate = dayjs(value);
          const age = dayjs().diff(birthDate, "years");

          return age >= 18;
        }),
      birth_state: yup.string().required("Campo requerido"),
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      sex: yup.string().oneOf(["MASCULINO", "FEMENINO"], "Debe seleccionar un género").required("Campo requerido"),
      code_phone_number: yup.string().required("Campo requerido"),
      phone_number: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);

        if (shareholderId) {
          // si ya se lleno el formulario de domicilio, y se modifica el rfc o curp(validando con la fecha de nacimiento), se debe validar la direccion.
          let address_verification_required = false;
          if (addressData && addressData.id) {
            const previus_date_birth = shareholderData.date_birth;
            const new_date_birth = values.date_birth;
            address_verification_required = previus_date_birth !== new_date_birth;
          }

          const payloadShareholderData: PayloadPersonPatch = {
            person_types: [personTypeId],
            name: values.name,
            last_name: values.first_lastname,
            last_name_2: values.second_lastname,
            birth_date: dayjs(values.date_birth).format("YYYY-MM-DD"),
            birth_state: Number(values.birth_state),
            email: values.email,
            sex: values.sex,
            rfc: "",
            curp: "",
            loan_application_id: application.id,
            code_phone_number: values.code_phone_number,
            phone_number: values.phone_number,
          };

          await patchPerson({
            personId: shareholderId,
            body: payloadShareholderData,
          }).unwrap();

          dispatch(
            setShareHolderData({
              id: shareholderId,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              date_birth: values.date_birth,
              birth_state: values.birth_state,
              email: values.email,
              sex: values.sex,
              rfc: "",
              rfc_homoclave: "",
              curp: "",
              bureau_inquiry: {
                prestamo_hipotecario: false,
                credito_auto: false,
                tarjeta_credito: false,
                tarjeta: "",
                autorizacion_buro: false,
              },
              code_phone_number: values.code_phone_number,
              phone_number: values.phone_number,
              address_verification_required,
            }),
          );
        } else {
          const payloadShareholderData: PayloadSaveShareholderData = {
            person_types: [personTypeId],
            name: values.name,
            last_name: values.first_lastname,
            last_name_2: values.second_lastname,
            birth_date: dayjs(values.date_birth).format("YYYY-MM-DD"),
            birth_state: Number(values.birth_state),
            email: values.email,
            sex: values.sex,
            rfc: "",
            curp: "",
            loan_application_id: application.id,
            is_shareholder: true,
            is_aval_onboarding: true,
            code_phone_number: values.code_phone_number,
            phone_number: values.phone_number,
          };

          const responseShareholderData: ResponseSaveShareholderData = await saveShareholderData(
            payloadShareholderData,
          ).unwrap();

          dispatch(
            setShareHolderData({
              id: responseShareholderData.data.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              date_birth: values.date_birth,
              birth_state: values.birth_state,
              email: values.email,
              sex: values.sex,
              rfc: "",
              rfc_homoclave: "",
              curp: "",
              bureau_inquiry: {
                prestamo_hipotecario: false,
                credito_auto: false,
                tarjeta_credito: false,
                tarjeta: "",
                autorizacion_buro: false,
              },
              code_phone_number: values.code_phone_number,
              phone_number: values.phone_number,
            }),
          );
        }

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus?.code));

        navigate(nextStatus?.url);
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("email", "El correo electrónico ya se encuentra registrado");
          return;
        }

        Sentry.captureException(error, {
          extra: {
            component: "DatosAccionista",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleMajorityShareholderChange = (checked: boolean) => {
    if (checked) {
      formik.setFieldValue("name", clientDetails.name);
      formik.setFieldValue("first_lastname", clientDetails.first_lastname);
      formik.setFieldValue("second_lastname", clientDetails.second_lastname);
      formik.setFieldValue("email", accountUser.email);
      formik.setFieldValue("code_phone_number", clientDetails.country_code || "+52");
      formik.setFieldValue("phone_number", clientDetails.phone);
    } else {
      formik.setFieldValue("name", "");
      formik.setFieldValue("first_lastname", "");
      formik.setFieldValue("second_lastname", "");
      formik.setFieldValue("email", "");
      formik.setFieldValue("code_phone_number", "+52");
      formik.setFieldValue("phone_number", "");
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const responseStates = await getStatesTrigger().unwrap();
        setPlacesCatalog(responseStates);
      } catch (error) {
        console.log("Error al obtener los datos iniciales");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (shareholderData?.id !== "" && shareholderData?.id !== undefined) {
      setShareholderId(String(shareholderData?.id));
      setSelectedDate(dayjs(shareholderData?.date_birth));
      formik.setFieldValue("date_birth", shareholderData?.date_birth);
    }
  }, [shareholderData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.datos_accionista?.titulo || "Registro del accionista mayoritario"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          {interfaceConfig.textos_tofu?.pm?.datos_accionista?.sub_titulo && (
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={300}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
              sx={{ whiteSpace: "pre-line", mt: 4 }}
            >
              {interfaceConfig.textos_tofu.pm.datos_accionista.sub_titulo}
            </Typography>
          )}

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <FormControl fullWidth>
                <FormControlLabel
                  sx={{ color: theme?.palette?.primary?.dark }}
                  control={<Checkbox />}
                  label="Soy el accionista mayoritario"
                  name="majority_shareholder"
                  checked={formik.values.majority_shareholder}
                  onChange={(_, checked: boolean) => {
                    formik.setFieldValue("majority_shareholder", checked);
                    handleMajorityShareholderChange(checked);
                  }}
                  disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  id="name"
                  name="name"
                  label="Nombre(s)"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("name", transformTitleCase(e.target.value))}
                  value={formik.values.name}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name ? formik.errors.name : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  id="first_lastname"
                  name="first_lastname"
                  label="Apellido paterno"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("first_lastname", transformTitleCase(e.target.value))}
                  value={formik.values.first_lastname}
                  error={formik.touched.first_lastname && Boolean(formik.errors.first_lastname)}
                  helperText={formik.touched.first_lastname ? formik.errors.first_lastname : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  id="second_lastname"
                  name="second_lastname"
                  label="Apellido materno"
                  margin="normal"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("second_lastname", transformTitleCase(e.target.value))}
                  value={formik.values.second_lastname}
                  error={formik.touched.second_lastname && Boolean(formik.errors.second_lastname)}
                  helperText={formik.touched.second_lastname ? formik.errors.second_lastname : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DatePicker
                    label="Fecha de nacimiento"
                    value={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      const formattedDate = dayjs(date).format("YYYY-MM-DD");

                      formik.setFieldValue("date_birth", formattedDate);

                      formik.validateField("date_birth");
                    }}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        name: "date_birth",
                        onBlur: formik.handleBlur,
                        error: formik.touched.date_birth && Boolean(formik.errors.date_birth),
                        helperText: formik.touched.date_birth ? formik.errors.date_birth : "",
                        required: true,
                      },
                    }}
                    disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                  />
                </LocalizationProvider>
              </FormControl>
              <FormControl
                fullWidth
                required
                sx={{ mt: 2 }}
                error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
              >
                <InputLabel id="birth_state-label">Lugar de nacimento</InputLabel>
                <Select
                  labelId="birth_state-label"
                  id="birth_state"
                  name="birth_state"
                  fullWidth
                  required
                  label="Lugar de nacimento"
                  value={formik.values.birth_state}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
                  disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                >
                  <MenuItem value={""}>Selecciona</MenuItem>
                  {placesCatalog.map((item: StateStructure) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(formik.touched.birth_state) && Boolean(formik.errors.birth_state) && (
                  <FormHelperText>{formik.errors.birth_state}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <TextField
                  id="email"
                  name="email"
                  label="Correo electrónico"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value_to_set = (e.target.value || "").toLowerCase();
                    formik.setFieldValue("email", value_to_set);
                  }}
                  value={formik.values.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email ? formik.errors.email : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={5}>
                  <CustomContryCode
                    label="Código"
                    name="code_phone_number"
                    value={formik.values.code_phone_number}
                    onChange={(ev) => {
                      if (buroNip.isValid) return;
                      formik.handleChange(ev);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.code_phone_number && Boolean(formik.errors.code_phone_number)}
                    disabled={formik.isSubmitting || shareholderData.check_sat_bureau || buroNip.isValid}
                    required={true}
                  />
                </Grid>
                <Grid item xs={7}>
                  <FormControl fullWidth>
                    <TextField
                      id="phone_number"
                      name="phone_number"
                      label="Teléfono"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={(ev) => {
                        if (buroNip.isValid) return;
                        formik.handleChange(ev);
                      }}
                      value={formik.values.phone_number}
                      error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                      helperText={formik.touched.phone_number ? formik.errors.phone_number : ""}
                      inputProps={{ maxLength: 10 }}
                      disabled={formik.isSubmitting || shareholderData.check_sat_bureau || buroNip.isValid}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl
                component="fieldset"
                sx={{ mt: 1 }}
                error={formik.touched.sex && formik.errors.sex ? true : false}
              >
                <FormLabel component="legend">Sexo como aparece en tu INE</FormLabel>
                <RadioGroup aria-label="sex" name="sex" value={formik.values.sex} onChange={formik.handleChange} row>
                  <FormControlLabel
                    sx={{ color: theme?.palette?.primary?.dark }}
                    value="MASCULINO"
                    control={<Radio />}
                    label="Masculino"
                    disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                  />
                  <FormControlLabel
                    sx={{ color: theme?.palette?.primary?.dark }}
                    value="FEMENINO"
                    control={<Radio />}
                    label="Femenino"
                    disabled={formik.isSubmitting || shareholderData.check_sat_bureau}
                  />
                </RadioGroup>
                {formik.touched.sex && formik.errors.sex ? <FormHelperText>Selecciona tu sexo</FormHelperText> : null}
              </FormControl>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || isLoading || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default DatosAccionista;
